// webserver/src/components/ControlPanel.js
import React, { Suspense, lazy, useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import "./ControlPanel.css";
import Settings from "../../components/Settings";
import AreaOverview from "../../components/AreaOverview";
import Dashboard from "../../components/Dashboard";
import SiteControl from '../../components/SiteControl/SiteControl';
import PluginEventsDashboard from "../../components/Events";
import Fixtures from '../../components/Fixtures/Fixtures';
import Scheduler from "../../components/Scheduler";
import PlaylistLibrary from "../../components/PlaylistLibrary";
import FallbackPlaylist from "../../components/FallbackPlaylist";
import Announcements from "../../components/Announcements";
import axios from "axios";

import ControllerControls from "../../components/ControllerControls";
import CreativeStudio from "../../components/CreativeStudio/CreativeStudio";
import MenusWorkspace from '../../components/Menus/MenusWorkspace';
import SocialContent from '../../components/SocialContent/SocialContent';
import Audience from "../../components/Audience/Audience";
import { UploadQueueProvider } from "../../context/UploadQueueContext";
import Subscriptions from "../../components/Subscriptions";
import Admin from "../../components/Admin";
import SponsoredVideos from '../../components/SponsoredVideos/SponsoredVideos';
import { getUserRoleFromToken } from "../../utils/jwt";
import { isPluginEnabled, loadEnabledPluginKeys, subscribeToPluginPreferences } from "../../utils/pluginPreferences";
import { OPEN_CREATIVE_STUDIO_GIG_CAMPAIGN_EVENT } from '../../components/Gigs/gigCampaignBridge';
import { OPEN_SOCIAL_CAMPAIGN_EVENT } from '../../components/SocialContent/socialCampaignBridge';
import { resolveWatchAction } from '../../components/Remote/useSharedRemoteRuntime';
import { canAccessPlaybackTab } from '../../utils/playbackEntitlements';
import { visiblePhysicalDevices, lgDeviceLabel, playbackDevice, lgPlayerId, isLgPlayer } from '../../utils/lgPlayer';

const WebsitePanel = lazy(() => import('../../components/WebsiteBuilder/WebsitePanel'));

function WebsitePanelFallback() {
  return (
    <div
      style={{
        border: '1px solid rgba(148, 163, 184, 0.18)',
        borderRadius: 18,
        padding: 20,
        background: 'rgba(15, 23, 42, 0.35)',
        color: '#cbd5e1',
      }}
    >
      Loading website builder…
    </div>
  );
}

const isRemoteControlDevice = (candidate) => {
  const normalizedType = String(candidate?.type || '').trim().toLowerCase();
  return [
    'staff',
    'phone remote',
    'staff phone',
    'phone_remote',
    'staff_phone',
    'control console',
    'console remote',
    'control_console',
    'console_remote',
  ].includes(normalizedType);
};

const getMasterSelectableDevices = (devicesList = []) =>
  visiblePhysicalDevices(Array.isArray(devicesList) ? devicesList : []).filter((candidate) => !isRemoteControlDevice(candidate));

function ControlPanel() {
  const history = useHistory();
  const location = useLocation();
  const didClearQueryRef = useRef(false);
  const isAdmin = getUserRoleFromToken(localStorage.getItem("token")) === "admin";
  const [site, setSite] = useState(null);
  const [sites, setSites] = useState([]);
  const [sharedDestinations, setSharedDestinations] = useState([]);
  const [entityMode, setEntityMode] = useState(
    isAdmin && localStorage.getItem('controlPanelEntityMode') === 'destination' ? 'destination' : 'venue'
  );
  const [area, setArea] = useState(null); // Current area
  const [areas, setAreas] = useState([]);
  const [device, setDevice] = useState(null);
  const [devices, setDevices] = useState([]);
  const [logicalTopology, setLogicalTopology] = useState({ screens: [], audioEndpoints: [] });
  const [deviceType, setDeviceType] = useState("");
  const [headerWatchTarget, setHeaderWatchTarget] = useState(null);
  const [headerWatchBusy, setHeaderWatchBusy] = useState(false);
  const [selectedTab, setSelectedTab] = useState("Settings");
  const [dashboardRequestedTab, setDashboardRequestedTab] = useState(null);
  const [powerDiagnosticsRequest, setPowerDiagnosticsRequest] = useState(null);
  const [openEventInstanceId, setOpenEventInstanceId] = useState(null);
  const [assistantLoginTrigger, setAssistantLoginTrigger] = useState(0);
  const [settingsRequestedSubTab, setSettingsRequestedSubTab] = useState(null);
  const [settingsRequestedDeviceId, setSettingsRequestedDeviceId] = useState(null);
  const [settingsDeviceRequestKey, setSettingsDeviceRequestKey] = useState(0);
  const [siteDetailsPlaceId, setSiteDetailsPlaceId] = useState(null);
  const [creativeStudioRequestedView, setCreativeStudioRequestedView] = useState(null);
  const [creativeStudioRequestedPlaqueId, setCreativeStudioRequestedPlaqueId] = useState(null);
  const [creativeStudioRequestedWidgetId, setCreativeStudioRequestedWidgetId] = useState(null);
  const [creativeStudioRequestKey, setCreativeStudioRequestKey] = useState(0);
  const [creativeStudioGenerationRequest, setCreativeStudioGenerationRequest] = useState(null);
  const [creativeStudioCampaignRequest, setCreativeStudioCampaignRequest] = useState(null);
  const [schedulerRequestedGigId, setSchedulerRequestedGigId] = useState(null);
  const [schedulerRequestKey, setSchedulerRequestKey] = useState(0);
  const [karaokeEnabled, setKaraokeEnabled] = useState(() => isPluginEnabled(site?.id || null, 'karaoke'));
  const [jukeboxEnabled, setJukeboxEnabled] = useState(() => isPluginEnabled(site?.id || null, 'jukebox'));
  const [fixturesEnabled, setFixturesEnabled] = useState(() => isPluginEnabled(site?.id || null, 'fixtures'));
  const [sitePlaybackEntitlements, setSitePlaybackEntitlements] = useState(null);

  const isDestinationMode = entityMode === 'destination';
  const siteLabel = isDestinationMode ? 'Destination' : 'Site';
  const areaLabel = isDestinationMode ? 'Activity' : 'Area';
  const resolveNoDeviceTabRef = useRef(null);
  const playbackTabContext = { isAdmin, entitlements: sitePlaybackEntitlements };
  const canUseScheduler = canAccessPlaybackTab('Scheduler', playbackTabContext);
  const canManageFallback = canAccessPlaybackTab('Fallback Playlist', playbackTabContext);

  useEffect(() => {
    let cancelled = false;
    setSitePlaybackEntitlements(null);
    if (!site?.id || isAdmin) return undefined;
    const token = localStorage.getItem('token');
    axios.get(`/api/entitlements/site?siteId=${encodeURIComponent(site.id)}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((response) => {
      if (!cancelled) setSitePlaybackEntitlements(response.data?.entitlements || {});
    }).catch(() => {
      if (!cancelled) setSitePlaybackEntitlements({});
    });
    return () => {
      cancelled = true;
    };
  }, [isAdmin, site?.id]);

  const rememberSelectedSite = useCallback((siteId) => {
    if (siteId) {
      localStorage.setItem('siteId', String(siteId));
    } else {
      localStorage.removeItem('siteId');
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const refreshEnabledPlugins = () => {
      if (cancelled) return;
      setKaraokeEnabled(isPluginEnabled(site?.id || null, 'karaoke'));
      setJukeboxEnabled(isPluginEnabled(site?.id || null, 'jukebox'));
      setFixturesEnabled(isPluginEnabled(site?.id || null, 'fixtures'));
    };
    refreshEnabledPlugins();
    if (site?.id) {
      loadEnabledPluginKeys(site.id).then(refreshEnabledPlugins);
    }
    const unsubscribe = subscribeToPluginPreferences(refreshEnabledPlugins);
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [site?.id]);

  const filterSitesByMode = useCallback((sitesList, mode) => {
    if (!Array.isArray(sitesList)) return [];
    return sitesList.filter((candidate) => {
      const candidateType =
        String(candidate?.site_type || 'venue').trim().toLowerCase() === 'destination'
          ? 'destination'
          : 'venue';
      return candidateType === mode;
    });
  }, []);

  const filteredSites = useMemo(
    () => filterSitesByMode(sites, entityMode),
    [filterSitesByMode, sites, entityMode]
  );
  const masterSelectableDevices = useMemo(
    () => getMasterSelectableDevices(devices),
    [devices]
  );

  useEffect(() => {
    let cancelled = false;
    setHeaderWatchTarget(null);
    if (!device?.id || isDestinationMode) return undefined;
    const token = localStorage.getItem('token');
    axios.get(`/api/site-control/targets/${device.id}/status`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((response) => {
      if (!cancelled) setHeaderWatchTarget(response.data?.target?.sourceLayout?.watchTarget || null);
    }).catch(() => {
      if (!cancelled) setHeaderWatchTarget(null);
    });
    return () => {
      cancelled = true;
    };
  }, [device?.id, isDestinationMode]);

  const handleHeaderWatch = useCallback(async () => {
    const televisionDeviceId = Number(headerWatchTarget?.televisionDeviceId || 0);
    const action = resolveWatchAction(headerWatchTarget);
    if (!televisionDeviceId || !action || headerWatchBusy) return;
    const token = localStorage.getItem('token');
    setHeaderWatchBusy(true);
    try {
      await axios.post(
        `/api/site-control/targets/${televisionDeviceId}/action`,
        { action },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Unable to switch the connected TV input.', error);
    } finally {
      setHeaderWatchBusy(false);
    }
  }, [headerWatchBusy, headerWatchTarget]);

  const applySiteFieldsUpdate = useCallback((siteId, fields = {}) => {
    if (!siteId || !fields || typeof fields !== 'object') return;
    setSites((prevSites) =>
      (Array.isArray(prevSites) ? prevSites : []).map((entry) =>
        String(entry?.id) === String(siteId)
          ? { ...entry, ...fields }
          : entry
      )
    );
    setSite((prevSite) =>
      prevSite && String(prevSite?.id) === String(siteId)
        ? { ...prevSite, ...fields }
        : prevSite
    );
  }, []);

  const movedControlPanelTabToSettingsSubTab = (tabName) => {
    if (tabName === "Wi-Fi Settings") return "Wi-Fi Settings";
    if (tabName === "Default Ads") return "Default Ads";
    if (tabName === "Plugins") return "Plugins";
    if (tabName === "Events") return "Plugins";
    return null;
  };

  const isAnnouncementDevice = (candidate) =>
    !!candidate &&
    (candidate.type === "TV" || candidate.type === "Music Player");

  const hasSitePowerControls = Boolean(site?.id && site?.site_type !== 'destination');

  const getNoDeviceAvailableTabs = useCallback(() => {
    const tabs = [...(isDestinationMode ? [] : ['Menus']), 'Creative Studio', 'Social Content', 'Website', 'Audience', 'Settings'];
    if (hasSitePowerControls) tabs.unshift('Site Control');
    if (canUseScheduler) tabs.unshift('Scheduler');
    if (karaokeEnabled) tabs.splice(1, 0, 'Karaoke');
    if (jukeboxEnabled) tabs.splice(karaokeEnabled ? 2 : 1, 0, 'Jukebox');
    if (fixturesEnabled) tabs.splice(1, 0, 'Fixtures');
    if (isAdmin) tabs.push('Sponsored Videos', 'Subscriptions', 'Admin');
    return tabs;
  }, [canUseScheduler, fixturesEnabled, hasSitePowerControls, isAdmin, isDestinationMode, jukeboxEnabled, karaokeEnabled]);

  const resolveNoDeviceTab = useCallback(
    (tabName) => (getNoDeviceAvailableTabs().includes(tabName) ? tabName : 'Settings'),
    [getNoDeviceAvailableTabs]
  );

  useEffect(() => {
    resolveNoDeviceTabRef.current = resolveNoDeviceTab;
  }, [resolveNoDeviceTab]);

  const getAvailableTabs = (deviceType, devicesList = [], currentDevice = null) => {
    if (deviceType === "lgtv") {
      const tabs = ["Area Overview", "Dashboard", ...(isDestinationMode ? [] : ['Menus']), 'Social Content', "Website", "Settings"];
      if (lgPlayerId(currentDevice)) tabs.splice(2, 0, 'Scheduler', 'Playlist Library', 'Fallback Playlist', 'Announcements');
      if (hasSitePowerControls) tabs.splice(1, 0, 'Site Control');
      if (karaokeEnabled) tabs.splice(tabs.length - 1, 0, "Karaoke");
      if (jukeboxEnabled) tabs.splice(tabs.length - 1, 0, "Jukebox");
      if (fixturesEnabled) tabs.splice(tabs.length - 1, 0, 'Fixtures');
      if (isAdmin) tabs.splice(tabs.length - 1, 0, 'Sponsored Videos', "Subscriptions", "Admin");
      return tabs;
    }
    if (deviceType === "tv-control" || deviceType === "stb") {
      const tabs = [
        "Area Overview",
        "Dashboard",
        ...(hasSitePowerControls ? ['Site Control'] : []),
        "Scheduler",
        ...(isDestinationMode ? [] : ['Menus']),
        'Creative Studio',
        'Social Content',
        'Website',
        "Audience",
        "Settings",
      ];
      if (karaokeEnabled) tabs.splice(2, 0, "Karaoke");
      if (jukeboxEnabled) tabs.splice(karaokeEnabled ? 3 : 2, 0, "Jukebox");
      if (fixturesEnabled) tabs.splice(2, 0, 'Fixtures');
      if (isAdmin) tabs.splice(tabs.length - 1, 0, 'Sponsored Videos', "Subscriptions", "Admin");
      return tabs;
    }
    if (deviceType && deviceType.startsWith("controller")) {
      const tabs = ['Area Overview', 'Controller', ...(isDestinationMode ? [] : ['Menus']), 'Creative Studio', 'Social Content', 'Website', 'Audience', 'Settings'];
      if (hasSitePowerControls) tabs.splice(2, 0, 'Site Control');
      if (karaokeEnabled) tabs.splice(2, 0, 'Karaoke');
      if (jukeboxEnabled) tabs.splice(karaokeEnabled ? 3 : 2, 0, 'Jukebox');
      if (fixturesEnabled) tabs.splice(2, 0, 'Fixtures');
      if (isAdmin) tabs.splice(tabs.length - 1, 0, 'Sponsored Videos', 'Subscriptions', 'Admin');
      return tabs;
    }
    const hasAnnouncementDevice = devicesList.some((d) => isAnnouncementDevice(d));

    const tabs = [
      "Area Overview",
      "Dashboard",
      "Site Control",
      "Scheduler",
      "Playlist Library",
    ];
    if (karaokeEnabled) {
      tabs.splice(3, 0, "Karaoke");
    }
    if (jukeboxEnabled) {
      tabs.splice(karaokeEnabled ? 4 : 3, 0, "Jukebox");
    }
    if (fixturesEnabled) tabs.splice(3, 0, 'Fixtures');
    if (hasAnnouncementDevice) {
      tabs.push("Announcements");
    }
    if (!isDestinationMode) tabs.push('Menus');
    tabs.push('Creative Studio');
    tabs.push('Social Content');
    tabs.push('Website');
    tabs.push('Audience');
    tabs.push('Fallback Playlist');
    if (isAdmin) tabs.push('Sponsored Videos', 'Subscriptions', 'Admin');
    tabs.push("Settings");
    return tabs;
  };

  const resolvePreferredTabForDevice = useCallback(
    (candidate, devicesList = devices) => {
      if (!candidate) {
        return resolveNoDeviceTab(selectedTab);
      }

      const availableTabs = getAvailableTabs(candidate.type, devicesList, candidate);

      if (availableTabs.includes('Dashboard') && candidate.type === 'TV') {
        return 'Dashboard';
      }
      if (availableTabs.includes('Announcements') && candidate.type === 'Music Player') {
        return 'Announcements';
      }
      if (availableTabs.includes('Controller') && candidate.type && candidate.type.startsWith('controller')) {
        return 'Controller';
      }
      if (availableTabs.includes('Area Overview')) {
        return 'Area Overview';
      }
      if (availableTabs.includes('Website')) {
        return 'Website';
      }
      if (availableTabs.includes('Settings')) {
        return 'Settings';
      }

      return availableTabs[0] || resolveNoDeviceTab(selectedTab);
    },
    [devices, resolveNoDeviceTab, selectedTab]
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requestedTab = params.get("tab");
    const requestedSettingsSubTab = params.get("settingsSubTab");
    const requestedPlaceId = params.get("placeId");
    const requestedCreativeStudioView = params.get("creativeStudioView");
    const requestedPlaqueId = params.get("plaqueId");

    if (requestedTab) {
      const movedSubTab = movedControlPanelTabToSettingsSubTab(requestedTab);
      if (movedSubTab) {
        setSelectedTab("Settings");
        setSettingsRequestedSubTab(movedSubTab);
      } else {
        setSelectedTab(requestedTab);
      }
    }
    if (requestedSettingsSubTab) {
      setSelectedTab("Settings");
      setSettingsRequestedSubTab(requestedSettingsSubTab);
    }
    if (requestedPlaceId) setSiteDetailsPlaceId(requestedPlaceId);
    if (requestedCreativeStudioView || requestedPlaqueId) {
      setCreativeStudioRequestedView(requestedCreativeStudioView || 'plaques');
      setCreativeStudioRequestedPlaqueId(requestedPlaqueId ? Number.parseInt(requestedPlaqueId, 10) || null : null);
      setCreativeStudioGenerationRequest(null);
      setCreativeStudioRequestKey((prev) => prev + 1);
    }

    if (!didClearQueryRef.current && (requestedTab || requestedSettingsSubTab || requestedPlaceId || requestedCreativeStudioView || requestedPlaqueId || params.get("siteId"))) {
      didClearQueryRef.current = true;
      setTimeout(() => history.replace("/control-panel"), 0);
    }
  }, [history, location.search]);

  useEffect(() => {
    if (!isAdmin && ['Sponsored Videos', 'Subscriptions', 'Admin'].includes(selectedTab)) {
      setSelectedTab('Settings');
    }
  }, [isAdmin, selectedTab]);

  useEffect(() => {
    if (!isAdmin && entityMode === 'destination') {
      setEntityMode('venue');
      localStorage.setItem('controlPanelEntityMode', 'venue');
    }
  }, [entityMode, isAdmin]);

  useEffect(() => {
    localStorage.setItem('controlPanelEntityMode', isAdmin ? entityMode : 'venue');
  }, [entityMode, isAdmin]);

  useEffect(() => {
    const movedSubTab = movedControlPanelTabToSettingsSubTab(selectedTab);
    if (!movedSubTab) return;
    setSelectedTab("Settings");
    setSettingsRequestedSubTab(movedSubTab);
  }, [selectedTab]);

  useEffect(() => {
    const fetchSitesAndAreas = async () => {
      const token = localStorage.getItem("token");
      try {
        const response = await fetch("/api/sites", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          const fetchedSites = data.sites || [];
          setSharedDestinations(data.sharedDestinations || []);
          setSites(fetchedSites);

          if (fetchedSites.length > 0) {
            const params = new URLSearchParams(window.location.search);
            const requestedSiteId = params.get("siteId") || localStorage.getItem("siteId");
            const requestedSite = fetchedSites.find((s) => String(s.id) === String(requestedSiteId));
            const requestedSiteMode =
              String(requestedSite?.site_type || 'venue').trim().toLowerCase() === 'destination'
                ? 'destination'
                : 'venue';
            const storedMode = localStorage.getItem('controlPanelEntityMode');
            const normalizedStoredMode = isAdmin && storedMode === 'destination' ? 'destination' : 'venue';
            const initialMode = !isAdmin
              ? 'venue'
              : requestedSite
              ? requestedSiteMode
              : filterSitesByMode(fetchedSites, normalizedStoredMode).length > 0
                ? normalizedStoredMode
                : String(fetchedSites[0]?.site_type || 'venue').trim().toLowerCase() === 'destination'
                  ? 'destination'
                  : 'venue';
            setEntityMode(initialMode);

            const initialFilteredSites = filterSitesByMode(fetchedSites, initialMode);
            const initialSite =
              initialFilteredSites.find((s) => String(s.id) === String(requestedSiteId)) ||
              initialFilteredSites[0] ||
              null;

            if (!initialSite) {
              setSite(null);
              setAreas([]);
              setArea(null);
              setDevices([]);
              setDevice(null);
              setDeviceType("");
              setSelectedTab("Settings");
              return;
            }
            setSite(initialSite);
            rememberSelectedSite(initialSite.id);

            const areasResponse = await fetch(
              `/api/sites/${initialSite.id}/areas`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );

            if (areasResponse.ok) {
              const areasData = await areasResponse.json();
              setAreas(areasData.areas || []);

              if (areasData.areas && areasData.areas.length > 0) {
                const firstArea = areasData.areas[0];
                setArea(firstArea);
                fetchDevices(firstArea.id);
              } else {
                setArea(null);
                setDevices([]);
                setDevice(null);
                setDeviceType("");
                setSelectedTab((current) => (
                  resolveNoDeviceTabRef.current ? resolveNoDeviceTabRef.current(current) : 'Scheduler'
                ));
              }
            }
          }
        } else {
          console.error("Failed to load sites.");
        }
      } catch (error) {
        console.error("Error fetching sites:", error);
      }
    };

    fetchSitesAndAreas();
  }, [filterSitesByMode, isAdmin, rememberSelectedSite]);

  useEffect(() => {
    const handler = (evt) => {
      const id = evt?.detail?.eventInstanceId || null;
      if (!id) return;
      setOpenEventInstanceId(id);
      setSelectedTab('Settings');
      setSettingsRequestedSubTab('Plugins');
    };
    window.addEventListener('ss:open-event-instance', handler);
    return () => window.removeEventListener('ss:open-event-instance', handler);
  }, []);

  // NEW: Fetch connection status using the current area's site_id
  useEffect(() => {
    if (area && area.site_id) {
      const token = localStorage.getItem("token");
      axios
        .get(
          `${process.env.REACT_APP_API_URL}/googlehome/status?site=${area.site_id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        )
        .then((res) => {
          if (res.data.google_home) {
            localStorage.setItem("googleConnected", "true");
          } else {
            localStorage.removeItem("googleConnected");
          }
          if (res.data.google_assistant) {
            localStorage.setItem("assistantConnected", "true");
          } else {
            localStorage.removeItem("assistantConnected");
          }
        })
        .catch((err) => {
          console.error("Error fetching Google connection status:", err);
        });
    } else {
      // Clear connection flags if no area/site_id is set.
      localStorage.removeItem("googleConnected");
      localStorage.removeItem("assistantConnected");
    }
  }, [area]);

  const fetchDevices = async (areaId, skipTabChange = false, preferredDeviceId = null) => {
    const token = localStorage.getItem("token");
    try {
      const response = await fetch(`/api/areas/${areaId}/devices`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        const devs = data.devices || [];
        const selectableDevs = getMasterSelectableDevices(devs);
        setDevices(devs);
        await fetch(`/api/areas/${areaId}/topology`, {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then((topologyResponse) => topologyResponse.ok ? topologyResponse.json() : null)
          .then((topology) => setLogicalTopology(topology || { screens: [], audioEndpoints: [] }))
          .catch((error) => {
            setLogicalTopology({ screens: [], audioEndpoints: [] });
            console.error('Failed to load Screen relationships:', error);
          });
        if (selectableDevs.length > 0) {
          const preferredDevice = preferredDeviceId
            ? selectableDevs.find((d) => Number(d.id) === Number(preferredDeviceId) || lgPlayerId(d) === Number(preferredDeviceId))
            : null;
          const existingDevice = device ? selectableDevs.find((d) => Number(d.id) === Number(device.id) || lgPlayerId(d) === Number(device.id)) : null;
          const nextDevice = preferredDevice || existingDevice || selectableDevs[0];
          setDevice(nextDevice);
          setDeviceType(nextDevice.type);
          if (!skipTabChange) {
            const availableTabs = getAvailableTabs(nextDevice.type, devs, nextDevice);
            if (!availableTabs.includes(selectedTab)) {
              setSelectedTab(resolvePreferredTabForDevice(nextDevice, devs));
            }
          }
          return nextDevice;
        } else {
          setDevice(null);
          setDeviceType("");
          if (!skipTabChange) {
            setSelectedTab((current) => resolveNoDeviceTab(current));
          }
        }
      } else {
        console.error("Failed to load devices.");
        setDevice(null);
        setDeviceType("");
        if (!skipTabChange) {
          setSelectedTab((current) => resolveNoDeviceTab(current));
        }
      }
    } catch (error) {
      console.error("Error fetching devices:", error);
      setDevice(null);
      setDeviceType("");
      if (!skipTabChange) {
        setSelectedTab((current) => resolveNoDeviceTab(current));
      }
    }
  };

  const reloadAfterPlayerLink = useRef();
  reloadAfterPlayerLink.current = () => { if (area?.id) fetchDevices(area.id, true, device?.id); };
  useEffect(() => {
    const refresh = () => reloadAfterPlayerLink.current?.();
    window.addEventListener('lg-player-link-changed', refresh);
    return () => window.removeEventListener('lg-player-link-changed', refresh);
  }, []);
  useEffect(() => {
    if (!device || lgPlayerId(device) || !(device.type === 'lgtv' || isLgPlayer(device))) return undefined;
    const timer = window.setInterval(() => {
      if (document.visibilityState !== 'hidden') reloadAfterPlayerLink.current?.();
    }, 15000);
    return () => window.clearInterval(timer);
  }, [device?.id, lgPlayerId(device)]);

  const handleSiteChange = async (siteId, sitesList = sites, skipTabChange = false) => {
    const updatedSite = sitesList.find((s) => s.id === siteId);
    setSite(updatedSite);
    if (updatedSite) {
      rememberSelectedSite(updatedSite.id);
      setAreas([]);
      setArea(null);
      setDevices([]);
      setDevice(null);
      setDeviceType('');
      if (!skipTabChange) {
        setSelectedTab((current) => resolveNoDeviceTab(current));
      }
      const token = localStorage.getItem('token');
      try {
        const response = await fetch(`/api/sites/${siteId}/areas`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setAreas(data.areas || []);
          if (data.areas && data.areas.length > 0) {
            const firstArea = data.areas[0];
            setArea(firstArea);
            await fetchDevices(firstArea.id, skipTabChange);
          } else {
            setArea(null);
            setDevices([]);
            setDevice(null);
            setDeviceType('');
            if (!skipTabChange) {
              setSelectedTab((current) => resolveNoDeviceTab(current));
            }
          }
        } else {
          console.error('Failed to fetch areas.');
          if (!skipTabChange) {
            setSelectedTab((current) => resolveNoDeviceTab(current));
          }
        }
      } catch (error) {
        console.error('Error fetching areas:', error);
        if (!skipTabChange) {
          setSelectedTab((current) => resolveNoDeviceTab(current));
        }
      }
    } else {
      setAreas([]);
      setDevices([]);
      setDevice(null);
      setDeviceType('');
      if (!skipTabChange) {
        setSelectedTab((current) => resolveNoDeviceTab(current));
      }
    }
  };

  useEffect(() => {
    const handler = (evt) => {
      const requestedSiteId = Number.parseInt(evt?.detail?.siteId, 10) || null;
      const requestedView = evt?.detail?.view || 'assets';

      if (requestedSiteId) {
        const requestedSite = (Array.isArray(sites) ? sites : []).find((entry) => String(entry?.id) === String(requestedSiteId)) || null;
        if (requestedSite) {
          const requestedMode =
            String(requestedSite?.site_type || 'venue').trim().toLowerCase() === 'destination'
              ? 'destination'
              : 'venue';
          if (requestedMode === 'destination' && !isAdmin) return;
          if (requestedMode !== entityMode) {
            setEntityMode(requestedMode);
          }
          handleSiteChange(requestedSiteId, filterSitesByMode(sites, requestedMode), true);
        } else {
          handleSiteChange(requestedSiteId, sites, true);
        }
      }

      setSelectedTab('Creative Studio');
      setCreativeStudioRequestedView(requestedView);
      setCreativeStudioRequestedPlaqueId(null);
      setCreativeStudioGenerationRequest(null);
      setCreativeStudioCampaignRequest(null);
      setCreativeStudioRequestKey((prev) => prev + 1);
    };

    window.addEventListener('ss:open-creative-studio-assets', handler);
    return () => window.removeEventListener('ss:open-creative-studio-assets', handler);
  }, [entityMode, filterSitesByMode, isAdmin, sites]);

  useEffect(() => {
    const handler = (evt) => {
      const detail = evt?.detail || {};
      const requestedSiteId = Number.parseInt(detail.siteId, 10) || null;

      if (requestedSiteId) {
        const requestedSite = (Array.isArray(sites) ? sites : []).find((entry) => String(entry?.id) === String(requestedSiteId)) || null;
        if (requestedSite) {
          const requestedMode =
            String(requestedSite?.site_type || 'venue').trim().toLowerCase() === 'destination'
              ? 'destination'
              : 'venue';
          if (requestedMode === 'destination' && !isAdmin) return;
          if (requestedMode !== entityMode) {
            setEntityMode(requestedMode);
          }
          handleSiteChange(requestedSiteId, filterSitesByMode(sites, requestedMode), true);
        } else {
          handleSiteChange(requestedSiteId, sites, true);
        }
      }

      setSelectedTab('Creative Studio');
      setCreativeStudioRequestedView('create');
      setCreativeStudioRequestedPlaqueId(null);
      setCreativeStudioGenerationRequest(detail);
      setCreativeStudioCampaignRequest(null);
      setCreativeStudioRequestKey((prev) => prev + 1);
    };

    window.addEventListener(OPEN_CREATIVE_STUDIO_GIG_CAMPAIGN_EVENT, handler);
    return () => window.removeEventListener(OPEN_CREATIVE_STUDIO_GIG_CAMPAIGN_EVENT, handler);
  }, [entityMode, filterSitesByMode, handleSiteChange, isAdmin, sites]);

  useEffect(() => {
    const handler = (evt) => {
      const detail = evt?.detail || {};
      if (!detail.campaign) return;
      setSelectedTab('Creative Studio');
      setCreativeStudioRequestedView('create');
      setCreativeStudioRequestedPlaqueId(null);
      setCreativeStudioGenerationRequest(null);
      setCreativeStudioCampaignRequest(detail.campaign);
      setCreativeStudioRequestKey((prev) => prev + 1);
    };
    window.addEventListener(OPEN_SOCIAL_CAMPAIGN_EVENT, handler);
    return () => window.removeEventListener(OPEN_SOCIAL_CAMPAIGN_EVENT, handler);
  }, []);

  const handleAreaChange = async (areaId, skipTabChange = false) => {
    const selectedArea = areas.find((a) => a.id === areaId);
    setArea(selectedArea);
    if (selectedArea) {
      await fetchDevices(selectedArea.id, skipTabChange);
    } else {
      setDevices([]);
      setDevice(null);
      setDeviceType('');
      if (!skipTabChange) {
        setSelectedTab((current) => resolveNoDeviceTab(current));
      }
    }
  };

  const handleDeviceChange = (deviceId) => {
    const selectedDevice = masterSelectableDevices.find((d) => Number(d.id) === Number(deviceId) || lgPlayerId(d) === Number(deviceId));
    setDevice(selectedDevice);
    setDeviceType(selectedDevice?.type || "");
    if (selectedDevice) {
      const availableTabs = getAvailableTabs(selectedDevice.type, devices, selectedDevice);
      if (!availableTabs.includes(selectedTab)) {
        setSelectedTab(resolvePreferredTabForDevice(selectedDevice, devices));
      }
    } else {
      setDeviceType("");
      setSelectedTab((current) => resolveNoDeviceTab(current));
    }
  };

  const groupedSelectableDevices = useMemo(() => {
    const byId = new Map(masterSelectableDevices.map((entry) => [Number(entry.id), entry]));
    const assigned = new Set();
    const groups = [];
    (logicalTopology.screens || []).forEach((screen) => {
      const entries = [screen.televisionDeviceId, ...(screen.playerDeviceIds || [screen.preferredPlayerDeviceId])]
        .map((id) => byId.get(Number(id)))
        .filter((entry) => entry && !assigned.has(Number(entry.id)));
      entries.forEach((entry) => assigned.add(Number(entry.id)));
      groups.push({ key: `screen-${screen.id || screen.name}`, label: `Screen: ${screen.name}`, entries });
    });
    (logicalTopology.audioEndpoints || []).forEach((endpoint) => {
      const source = byId.get(Number(endpoint.sourceDeviceId));
      const entries = source && !assigned.has(Number(source.id)) ? [source] : [];
      entries.forEach((entry) => assigned.add(Number(entry.id)));
      if (entries.length) groups.push({ key: `audio-${endpoint.id || endpoint.name}`, label: endpoint.name, entries });
    });
    const unassigned = masterSelectableDevices.filter((entry) => !assigned.has(Number(entry.id)));
    if (unassigned.length) groups.push({ key: 'other', label: 'Other devices', entries: unassigned });
    return groups;
  }, [logicalTopology, masterSelectableDevices]);

  const handleDeviceSelectFromAreaOverview = (deviceId) => {
    const selectedDevice = masterSelectableDevices.find((d) => Number(d.id) === Number(deviceId));
    handleDeviceChange(deviceId);
    if (selectedDevice) {
      setSelectedTab(resolvePreferredTabForDevice(selectedDevice, devices));
    }
  };

  const handleOpenDeviceRemote = async (deviceId, targetAreaId) => {
    const targetDevice = masterSelectableDevices.find((entry) => Number(entry.id) === Number(deviceId));
    if (targetDevice) {
      handleDeviceChange(targetDevice.id);
    } else {
      // A just-paired TV may precede the chooser refresh, or belong to another site area.
      const targetArea = areas.find((entry) => Number(entry.id) === Number(targetAreaId));
      if (!targetArea) return;
      setArea(targetArea);
      const refreshedTarget = await fetchDevices(targetArea.id, true, deviceId);
      if (Number(refreshedTarget?.id) !== Number(deviceId)) return;
    }
    setDashboardRequestedTab('TV Remote');
    setSelectedTab('Dashboard');
  };

  const handleOpenDevicePairing = (deviceId) => {
    const targetDevice = masterSelectableDevices.find((entry) => Number(entry.id) === Number(deviceId));
    if (!targetDevice) return;
    handleDeviceChange(targetDevice.id);
    setPowerDiagnosticsRequest(null);
    setSelectedTab('Site Control');
  };

  const handleOpenAndroidPowerSettings = (deviceId) => {
    const targetDevice = masterSelectableDevices.find((entry) => Number(entry.id) === Number(deviceId));
    if (!targetDevice) return;
    handleDeviceChange(targetDevice.id);
    setPowerDiagnosticsRequest((current) => ({ deviceId: targetDevice.id, key: (current?.key || 0) + 1 }));
    setSelectedTab('Site Control');
  };

  const handleTabClick = (tabName) => {
    setDashboardRequestedTab(null);
    setSelectedTab(tabName);
  };

  const handleDeviceChangeAndTab = (deviceId, tabName) => {
    handleDeviceChange(deviceId);
    if (!tabName || tabName === 'TV Controls') {
      const nextDevice = masterSelectableDevices.find((d) => Number(d.id) === Number(deviceId));
      if (nextDevice) {
        setSelectedTab(resolvePreferredTabForDevice(nextDevice, devices));
      }
      return;
    }
    setSelectedTab(tabName);
  };

  const handleOpenSchedulerGig = useCallback((gigId) => {
    const normalizedGigId = Number.parseInt(gigId, 10) || null;
    setSelectedTab('Scheduler');
    if (!normalizedGigId) return;
    setSchedulerRequestedGigId(normalizedGigId);
    setSchedulerRequestKey((prev) => prev + 1);
  }, []);

  const refreshSitesAreasDevices = useCallback(
    async (siteId, areaId, stayOnSettings = false, preferredDeviceId = null) => {
      const token = localStorage.getItem('token');
      let sitesList = sites;
      try {
        const res = await fetch('/api/sites', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          sitesList = data.sites || [];
          setSharedDestinations(data.sharedDestinations || []);
          setSites(sitesList);
        } else {
          console.error('Failed to refresh sites.');
        }
      } catch (err) {
        console.error('Error refreshing sites:', err);
      }
      const modeSites = filterSitesByMode(sitesList, entityMode);
      const nextSite =
        modeSites.find((s) => String(s.id) === String(siteId)) || modeSites[0];
      setSite(nextSite || null);
      rememberSelectedSite(nextSite?.id || null);
      if (!nextSite) {
        setAreas([]);
        setArea(null);
        setDevices([]);
        setDevice(null);
        setDeviceType('');
        if (!stayOnSettings) {
          setSelectedTab((current) => resolveNoDeviceTab(current));
        }
        return;
      }

      try {
        const response = await fetch(`/api/sites/${nextSite.id}/areas`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          const nextAreas = data.areas || [];
          setAreas(nextAreas);
          const nextArea =
            nextAreas.find((a) => String(a.id) === String(areaId)) || nextAreas[0];
          setArea(nextArea || null);
          if (nextArea) {
            await fetchDevices(nextArea.id, stayOnSettings, preferredDeviceId);
          } else {
            setDevices([]);
            setDevice(null);
            setDeviceType('');
            if (!stayOnSettings) {
              setSelectedTab((current) => resolveNoDeviceTab(current));
            }
          }
        } else {
          console.error('Failed to fetch areas.');
          if (!stayOnSettings) {
            setSelectedTab((current) => resolveNoDeviceTab(current));
          }
        }
      } catch (error) {
        console.error('Error fetching areas:', error);
        if (!stayOnSettings) {
          setSelectedTab((current) => resolveNoDeviceTab(current));
        }
      }
      if (stayOnSettings) setSelectedTab('Settings');
    },
    [entityMode, fetchDevices, filterSitesByMode, rememberSelectedSite, resolveNoDeviceTab, sites]
  );

  const handleEntityModeToggle = async (nextMode) => {
    if (nextMode === 'destination' && !isAdmin) return;
    if (nextMode === entityMode) return;
    setEntityMode(nextMode);
    const nextModeSites = filterSitesByMode(sites, nextMode);
    if (nextModeSites.length > 0) {
      await handleSiteChange(nextModeSites[0].id, nextModeSites);
      return;
    }
    setSite(null);
    setAreas([]);
    setArea(null);
    setDevices([]);
    setDevice(null);
    setDeviceType('');
    setSelectedTab('Settings');
  };

  useEffect(() => {
    if (device) {
      const availableTabs = getAvailableTabs(deviceType, devices, device);
      if (!availableTabs.includes(selectedTab)) {
        setSelectedTab(resolvePreferredTabForDevice(device, devices));
      }
    } else {
      if (
        !getNoDeviceAvailableTabs().includes(selectedTab)
      ) {
        setSelectedTab('Settings');
      }
    }
  }, [device, deviceType, devices, getNoDeviceAvailableTabs, isAdmin, resolvePreferredTabForDevice, selectedTab]);

  useEffect(() => {
    if ((selectedTab === 'Scheduler' && !canUseScheduler)
      || (selectedTab === 'Fallback Playlist' && !canManageFallback)) {
      setSelectedTab('Settings');
    }
  }, [canManageFallback, canUseScheduler, selectedTab]);

  const renderTabButton = (tabName, label = tabName) => (
    <button
      key={tabName}
      className={`tab-button ${selectedTab === tabName ? 'active' : ''}`}
      onClick={() => handleTabClick(tabName)}
    >
      {label}
    </button>
  );

  const renderTabs = () => {
    const primaryTabs = isDestinationMode ? [] : [renderTabButton('Menus')];
    const primaryRightTabs = [];
    const adminTabsLeft = [];
    const adminTabsRight = [];

    if (!device) {
      if (canUseScheduler) primaryTabs.push(renderTabButton('Scheduler'));
      if (karaokeEnabled) primaryTabs.push(renderTabButton('Karaoke'));
      if (jukeboxEnabled) primaryTabs.push(renderTabButton('Jukebox'));
      if (fixturesEnabled) primaryTabs.push(renderTabButton('Fixtures'));
      if (hasSitePowerControls) primaryTabs.push(renderTabButton('Site Control'));
      primaryTabs.push(renderTabButton('Creative Studio'));
      primaryTabs.push(renderTabButton('Social Content'));
      primaryTabs.push(renderTabButton('Website'));
      primaryTabs.push(renderTabButton('Audience'));
      primaryRightTabs.push(renderTabButton('Settings'));
      if (isAdmin) {
        adminTabsLeft.push(renderTabButton('Sponsored Videos'));
        adminTabsRight.push(renderTabButton('Subscriptions'));
        adminTabsRight.push(renderTabButton('Admin'));
      }
      return { primaryTabs, primaryRightTabs, adminTabsLeft, adminTabsRight };
    }

    if (deviceType && deviceType.startsWith("controller")) {
      primaryTabs.push(renderTabButton('Controller'));
      if (karaokeEnabled) primaryTabs.push(renderTabButton('Karaoke'));
      if (jukeboxEnabled) primaryTabs.push(renderTabButton('Jukebox'));
      if (fixturesEnabled) primaryTabs.push(renderTabButton('Fixtures'));
      if (hasSitePowerControls) primaryTabs.push(renderTabButton('Site Control'));
      primaryTabs.push(renderTabButton('Creative Studio'));
      primaryTabs.push(renderTabButton('Social Content'));
      primaryTabs.push(renderTabButton('Website'));
      primaryTabs.push(renderTabButton('Audience'));
      primaryRightTabs.push(renderTabButton('Settings'));
      if (isAdmin) {
        adminTabsLeft.push(renderTabButton('Sponsored Videos'));
        adminTabsRight.push(renderTabButton('Subscriptions'));
        adminTabsRight.push(renderTabButton('Admin'));
      }
      return { primaryTabs, primaryRightTabs, adminTabsLeft, adminTabsRight };
    }
    if (deviceType === "lgtv") {
      primaryTabs.push(renderTabButton('Area Overview'));
      primaryTabs.push(renderTabButton('Dashboard'));
      if (playbackDevice(device, devices).id !== device.id) {
        if (canUseScheduler) primaryTabs.push(renderTabButton('Scheduler'));
        primaryTabs.push(renderTabButton('Playlist Library'));
        if (canManageFallback) primaryTabs.push(renderTabButton('Fallback Playlist'));
        primaryTabs.push(renderTabButton('Announcements'));
      }
      if (hasSitePowerControls) primaryTabs.push(renderTabButton('Site Control'));
      primaryTabs.push(renderTabButton('Social Content'));
      primaryTabs.push(renderTabButton('Website'));
      if (karaokeEnabled) primaryTabs.push(renderTabButton('Karaoke'));
      if (jukeboxEnabled) primaryTabs.push(renderTabButton('Jukebox'));
      if (fixturesEnabled) primaryTabs.push(renderTabButton('Fixtures'));
      primaryRightTabs.push(renderTabButton('Settings'));
      if (isAdmin) {
        adminTabsLeft.push(renderTabButton('Sponsored Videos'));
        adminTabsRight.push(renderTabButton('Subscriptions'));
        adminTabsRight.push(renderTabButton('Admin'));
      }
      return { primaryTabs, primaryRightTabs, adminTabsLeft, adminTabsRight };
    }

    primaryTabs.push(renderTabButton('Area Overview'));

    primaryTabs.push(renderTabButton('Dashboard'));
    if (hasSitePowerControls || (deviceType !== 'tv-control' && deviceType !== 'stb')) primaryTabs.push(renderTabButton('Site Control'));

    if (canUseScheduler) primaryTabs.push(renderTabButton('Scheduler'));
    if (karaokeEnabled) {
      primaryTabs.push(renderTabButton('Karaoke'));
    }
    if (jukeboxEnabled) {
      primaryTabs.push(renderTabButton('Jukebox'));
    }
    if (fixturesEnabled) primaryTabs.push(renderTabButton('Fixtures'));

    const hasLibraries = !(deviceType === "tv-control" || deviceType === "stb" || deviceType === "lgtv");
    if (hasLibraries) {
      primaryTabs.push(renderTabButton('Playlist Library'));
    }

    const hasAnnouncementDevice = devices.some((d) => isAnnouncementDevice(d));
    if (hasAnnouncementDevice) {
      primaryTabs.push(renderTabButton('Announcements'));
    }

    primaryTabs.push(renderTabButton('Creative Studio'));
    primaryTabs.push(renderTabButton('Social Content'));
    primaryTabs.push(renderTabButton('Website'));
    primaryTabs.push(renderTabButton('Audience'));

    if (hasLibraries && canManageFallback) {
      primaryTabs.push(renderTabButton('Fallback Playlist'));
    }

    if (isAdmin) {
      adminTabsLeft.push(renderTabButton('Sponsored Videos'));
      adminTabsRight.push(renderTabButton('Subscriptions'));
      adminTabsRight.push(renderTabButton('Admin'));
    }

    primaryRightTabs.push(renderTabButton('Settings'));

    return { primaryTabs, primaryRightTabs, adminTabsLeft, adminTabsRight };
  };

  return (
    <UploadQueueProvider>
      <div className="control-panel">
        <div className="selectors-row">
          {isAdmin && (
            <div className="selector-item selector-item--mode">
              <label htmlFor="control-panel-mode">Mode</label>
              <div id="control-panel-mode" className="mode-toggle" role="group" aria-label="Control panel mode">
                <button
                  type="button"
                  className={`mode-toggle__option ${entityMode === 'venue' ? 'active' : ''}`}
                  onClick={() => handleEntityModeToggle('venue')}
                >
                  Venue
                </button>
                <button
                  type="button"
                  className={`mode-toggle__option ${entityMode === 'destination' ? 'active' : ''}`}
                  onClick={() => handleEntityModeToggle('destination')}
                >
                  Destination
                </button>
              </div>
            </div>
          )}
          <div className="selector-item">
            <label htmlFor="control-panel-site">{siteLabel}</label>
            <select
              id="control-panel-site"
              value={site?.id || ""}
              onChange={(e) => handleSiteChange(parseInt(e.target.value, 10), filteredSites, true)}
            >
              <option value="" disabled>
                {`Select a ${siteLabel.toLowerCase()}`}
              </option>
              {filteredSites.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
            </select>
          </div>
          <div className="selector-item">
            <label htmlFor="control-panel-area">{areaLabel}</label>
            <select
              id="control-panel-area"
              value={area?.id || ""}
              onChange={(e) => handleAreaChange(parseInt(e.target.value, 10), true)}
            >
              <option value="" disabled>
                {`Select an ${areaLabel.toLowerCase()}`}
              </option>
              {areas.map((area) => (
                <option key={area.id} value={area.id}>
                  {area.name}
                </option>
              ))}
            </select>
          </div>
          {!isDestinationMode && (
            <div className="selector-item">
              <label htmlFor="control-panel-device">Device</label>
              <div className="selector-item__control">
                <select
                  id="control-panel-device"
                  value={device?.id || ""}
                  onChange={(e) => handleDeviceChange(parseInt(e.target.value, 10))}
                >
                  <option value="" disabled>
                    Select a device
                  </option>
                  {groupedSelectableDevices.map((group) => group.entries.length === 1 ? (
                    <option key={group.key} value={group.entries[0].id}>{lgDeviceLabel(group.entries[0])}</option>
                  ) : (
                    <optgroup key={group.key} label={group.label}>
                      {group.entries.map((entry) => (
                        <option key={entry.id} value={entry.id}>{lgDeviceLabel(entry)}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                {headerWatchTarget && (
                  <button
                    type="button"
                    className="selector-watch-button"
                    disabled={headerWatchBusy}
                    onClick={handleHeaderWatch}
                    title={`Show ${headerWatchTarget.playerName || device?.name || 'this device'} on ${headerWatchTarget.televisionName || 'the connected TV'}`}
                  >
                    {headerWatchBusy ? 'Switching…' : 'Watch'}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
        {(() => {
          const { primaryTabs, primaryRightTabs, adminTabsLeft, adminTabsRight } = renderTabs();
          return (
            <div className="tabs">
              <div className="tabs-row">
                {primaryTabs}
                {primaryRightTabs.length > 0 && <span className="tabs-spacer" />}
                {primaryRightTabs}
              </div>
              {(adminTabsLeft.length > 0 || adminTabsRight.length > 0) && (
                <div className="tabs-row tabs-row--admin">
                  {adminTabsLeft}
                  {adminTabsRight.length > 0 && <span className="tabs-spacer" />}
                  {adminTabsRight}
                </div>
              )}
            </div>
          );
        })()}
        <div className="tab-content">
          {selectedTab === "Settings" && (
            <Settings
              site={site}
              sites={sites}
              sharedDestinations={sharedDestinations}
              area={area}
              entityMode={entityMode}
              onUpdateSiteFields={applySiteFieldsUpdate}
              currentDevice={device}
              assistantLoginTrigger={assistantLoginTrigger}
              requestedSubTab={settingsRequestedSubTab}
              requestedDeviceId={settingsRequestedDeviceId}
              requestedDeviceRequestKey={settingsDeviceRequestKey}
              siteDetailsPlaceId={siteDetailsPlaceId}
              onConsumeSiteDetailsPlaceId={() => setSiteDetailsPlaceId(null)}
              openEventInstanceId={openEventInstanceId}
              onConsumeOpenEventRequest={() => setOpenEventInstanceId(null)}
              refreshSitesAreasDevices={refreshSitesAreasDevices}
            />
          )}
          {selectedTab === "Area Overview" && area && (
            <AreaOverview
              area={area}
              siteId={site?.id}
              currentDeviceId={device?.id || null}
              onDeviceSelect={handleDeviceSelectFromAreaOverview}
              onOpenDevicePairing={handleOpenDevicePairing}
              onOpenDeviceRemote={handleOpenDeviceRemote}
              onOpenAndroidPowerSettings={handleOpenAndroidPowerSettings}
              onTopologyChange={setLogicalTopology}
            />
          )}
          {selectedTab === "Dashboard" && device && (
              <Dashboard
                requestedTab={dashboardRequestedTab}
                device={device}
                deviceType={deviceType}
                playerDevice={playbackDevice(device, devices)}
                site={site}
                onSourceTargetSelected={handleDeviceChange}
                onRequestAssistantLogin={() => {
                  setSelectedTab("Settings");
                  setSettingsRequestedSubTab("Integrations");
                  setAssistantLoginTrigger((t) => t + 1);
                }}
                onDeviceUpdated={(updatedDevice) => {
                  setDevice((current) => lgPlayerId(current) === Number(updatedDevice.id) ? current : updatedDevice);
                  setDevices((prev) =>
                    prev.map((d) => (d.id === updatedDevice.id ? updatedDevice : d))
                  );
                }}
              />
            )}
          {selectedTab === 'Site Control' && site?.id && (
              <SiteControl
                device={device}
                site={site}
                area={area}
                siteAreas={areas}
                devices={devices}
                topology={logicalTopology}
                onTopologyChange={setLogicalTopology}
                onEquipmentChange={(changedSiteId) => {
                  if (Number(changedSiteId) === Number(site?.id) && area?.id) return fetchDevices(area.id, true);
                }}
                onDeviceSelect={handleDeviceSelectFromAreaOverview}
                onDeviceRenamed={(deviceId, nextName) => {
                  setDevices((previous) => previous.map((entry) => Number(entry.id) === Number(deviceId) ? { ...entry, name: nextName } : entry));
                  setDevice((previous) => Number(previous?.id) === Number(deviceId) ? { ...previous, name: nextName } : previous);
                }}
                onOpenDeviceRemote={handleOpenDeviceRemote}
                onOpenAndroidPowerSettings={handleOpenAndroidPowerSettings}
                powerDiagnosticsRequest={powerDiagnosticsRequest?.deviceId === device?.id ? powerDiagnosticsRequest : null}
                onManageDeviceApps={() => {
                  setSelectedTab('Settings');
                  setSettingsRequestedSubTab('Devices');
                }}
              />
            )}
          {selectedTab === "Karaoke" && karaokeEnabled && (
            <PluginEventsDashboard
              siteId={site?.id || null}
              deviceId={device?.id || null}
              mode="karaoke-dashboard"
            />
          )}
          {selectedTab === "Jukebox" && jukeboxEnabled && (
            <PluginEventsDashboard
              siteId={site?.id || null}
              deviceId={device?.id || null}
              mode="jukebox-dashboard"
            />
          )}
          {selectedTab === 'Fixtures' && fixturesEnabled && (
            <Fixtures siteId={site?.id || null} deviceId={device?.id || null} timeZone={site?.time_zone || site?.timeZone} />
          )}
          {selectedTab === "Scheduler" && canUseScheduler && (
            <Scheduler
              device={playbackDevice(device, devices)}
              area={area}
              site={site}
              onDeviceChange={handleDeviceChange}
              requestedGigId={schedulerRequestedGigId}
              openRequestKey={schedulerRequestKey}
              onEditDeviceApps={(deviceId) => {
                handleDeviceChange(deviceId);
                setSettingsRequestedSubTab('Devices');
                setSettingsRequestedDeviceId(deviceId);
                setSettingsDeviceRequestKey((value) => value + 1);
                setSelectedTab('Settings');
              }}
            />
          )}
          {selectedTab === "Playlist Library" && device && (
            <PlaylistLibrary selectedDevice={playbackDevice(device, devices)} site={site} />
          )}
          {selectedTab === "Fallback Playlist" && device && canManageFallback && (
            <FallbackPlaylist selectedDevice={playbackDevice(device, devices)} site={site} isAdmin={isAdmin} />
          )}
          {selectedTab === "Announcements" &&
            devices.some((entry) => isAnnouncementDevice(entry)) && (
              <Announcements selectedDevice={playbackDevice(device, devices)} devices={devices} />
            )}
          {selectedTab === 'Menus' && !isDestinationMode && (
            <MenusWorkspace
              site={site ? { ...site, areas } : null}
              area={area}
              onEditWidget={(widgetId) => {
                if (!isAdmin) return;
                setCreativeStudioRequestedWidgetId(widgetId);
                setCreativeStudioRequestedView('widgets');
                setCreativeStudioRequestedPlaqueId(null);
                setCreativeStudioGenerationRequest(null);
                setCreativeStudioCampaignRequest(null);
                setCreativeStudioRequestKey((prev) => prev + 1);
                setSelectedTab('Creative Studio');
              }}
            />
          )}
          {selectedTab === 'Creative Studio' && (
            <CreativeStudio
              site={site ? { ...site, areas } : null}
              area={area}
              selectedDevice={device}
              requestedView={creativeStudioRequestedView}
              openPlaqueId={creativeStudioRequestedPlaqueId}
              openWidgetId={creativeStudioRequestedWidgetId}
              generationRequest={creativeStudioGenerationRequest}
              campaignRequest={creativeStudioCampaignRequest}
              onOpenSourceGig={handleOpenSchedulerGig}
              openRequestKey={creativeStudioRequestKey}
            />
          )}
          {selectedTab === 'Social Content' && <SocialContent site={site} selectedDevice={device} />}
          {selectedTab === 'Website' && (
            <Suspense fallback={<WebsitePanelFallback />}>
              <WebsitePanel
                site={site}
                onUpdateSiteFields={applySiteFieldsUpdate}
              />
            </Suspense>
          )}
          {selectedTab === 'Audience' && <Audience siteId={site?.id || null} />}
          {selectedTab === 'Sponsored Videos' && isAdmin && <SponsoredVideos />}
          {selectedTab === 'Subscriptions' && isAdmin && <Subscriptions />}
          {selectedTab === 'Admin' && isAdmin && <Admin />}
          {selectedTab === "Controller" &&
            device &&
            deviceType &&
            deviceType.startsWith("controller") && (
              <ControllerControls
                selectedDevice={device}
                area={area}
                site={site}
                refreshSitesAreasDevices={refreshSitesAreasDevices}
              />
            )}

          {device == null &&
            selectedTab !== "Settings" &&
            (!isAdmin || !['Sponsored Videos', 'Subscriptions', 'Admin'].includes(selectedTab)) &&
            selectedTab !== 'Website' &&
            selectedTab !== 'Menus' &&
            selectedTab !== 'Karaoke' &&
            selectedTab !== 'Jukebox' &&
            selectedTab !== 'Fixtures' &&
            selectedTab !== "Controller" && (
              <p>No devices found in this area. Please select a device.</p>
            )}
        </div>
      </div>
    </UploadQueueProvider>
  );
}

export default ControlPanel;
